import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/utils/cn'
import { Spinner } from './Spinner'

const button = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white shadow-sm hover:bg-primary-600',
        secondary: 'bg-secondary text-white shadow-sm hover:bg-secondary-700',
        accent: 'bg-accent text-white shadow-sm hover:bg-accent-600',
        danger: 'bg-danger text-white shadow-sm hover:bg-danger-600',
        outline: 'border border-border bg-card text-foreground shadow-sm hover:bg-primary-50 hover:border-primary-200 dark:hover:bg-primary-500/15 dark:hover:border-primary-500/40',
        ghost: 'text-slate-600 hover:bg-muted hover:text-foreground',
        subtle: 'bg-primary-50 text-primary-700 hover:bg-primary-100 dark:hover:bg-primary-500/25',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4',
        lg: 'h-11 px-6 text-base',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  loading?: boolean
  icon?: React.ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, icon, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(button({ variant, size }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Spinner className="h-4 w-4" /> : icon}
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'
